import { useRef } from 'react';
import { ApiError } from './api';

/**
 * One Idempotency-Key per user intent (RES-073): a double submit, or a
 * retry after the answer was lost, sends the same key, and the API applies
 * the request once. The key is renewed once the API has decided — success,
 * or a refusal the person may fix and send again — and kept while the
 * outcome is unknown (no answer, a server or gateway error, or the same
 * request still running).
 */
export function newIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

/** Whether the API decided the request (the key may be retired). */
export function decided(err?: unknown): boolean {
  if (err === undefined) return true;
  if (!(err instanceof ApiError)) return false;
  if (err.status === 0 || err.status >= 500) return false;
  return !(err.status === 409 && err.code === 'request_in_progress');
}

export function useIdempotencyKey(): { key: () => string; settle: (err?: unknown) => void } {
  const ref = useRef<string>('');
  if (!ref.current) ref.current = newIdempotencyKey();
  return {
    key: () => ref.current,
    settle: (err?: unknown) => {
      if (decided(err)) ref.current = newIdempotencyKey();
    },
  };
}
